
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FileUp, UploadCloud, FileText, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface DocumentUploaderProps {
  uploadedDocuments: File[];
  setUploadedDocuments: React.Dispatch<React.SetStateAction<File[]>>;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  uploadedDocuments,
  setUploadedDocuments
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setUploadedDocuments(prev => [...prev, ...newFiles]);
      
      toast({
        title: "Documents Added",
        description: `${newFiles.length} document(s) added to your agent configuration.`
      });
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const removeDocument = (indexToRemove: number) => {
    setUploadedDocuments(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1">
        <FileUp className="h-4 w-4" /> Import Documents
      </Label>
      <div className="flex flex-col gap-2">
        <div 
          className="border-2 border-dashed border-[#0EA5E9]/30 rounded-md p-4 text-center cursor-pointer hover:bg-[#0EA5E9]/5 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadCloud className="h-8 w-8 mx-auto text-[#0EA5E9]/50 mb-2" />
          <p className="text-sm text-gray-600">Click to upload documents, forms or SOPs</p>
          <p className="text-xs text-gray-500">PDF, DOCX, TXT, XLSX files supported</p>
        </div>
        <input 
          type="file" 
          ref={fileInputRef} 
          className="hidden" 
          multiple 
          accept=".pdf,.docx,.txt,.xlsx" 
          onChange={handleFileUpload} 
        />
        {uploadedDocuments.length > 0 && (
          <div className="mt-2 space-y-2">
            <p className="text-sm font-medium">Uploaded Documents:</p>
            <div className="space-y-1">
              {uploadedDocuments.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-[#0EA5E9]/10 rounded text-sm">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-[#0EA5E9]" />
                    <span className="truncate max-w-[200px]">{doc.name}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0 text-red-500" 
                    onClick={() => removeDocument(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUploader;
